import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { axiosInstance } from '../../api/api';
import { Routes, TabScreens } from '../../routes';
import { actions } from '../../store/actions';
import { usePermission } from '../../hooks/usePermission';
import { SearchSvg } from '../../assets/svg/SearchSvg';

interface PatrolGroup {
  id: string;
  name: string;
  created_at?: string;
}

interface Patrol {
  id: number;
  name: string;
}

interface User {
  id: number;
  userId?: number;
  name: string;
  fullName?: string;
}

const AlertModal: React.FC<{
  type: 'success' | 'error' | 'warning' | 'info' | 'confirm';
  title: string;
  message: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose: () => void;
  confirmText?: string;
  cancelText?: string;
}> = ({ type, title, message, onConfirm, onCancel, onClose, confirmText = 'Ya', cancelText = 'Batal' }) => {
  const getColors = () => {
    switch (type) {
      case 'success':
        return {
          bg: '#F0FDF4',
          border: '#BBF7D0',
          text: '#166534',
          button: '#10B981',
          buttonHover: '#059669'
        };
      case 'error':
        return {
          bg: '#FEF2F2',
          border: '#FECACA',
          text: '#DC2626',
          button: '#EF4444',
          buttonHover: '#DC2626'
        };
      case 'warning':
        return {
          bg: '#FFFBEB',
          border: '#FED7AA',
          text: '#D97706',
          button: '#F59E0B',
          buttonHover: '#D97706'
        };
      case 'info':
        return {
          bg: '#EFF6FF',
          border: '#BFDBFE',
          text: '#1E40AF',
          button: '#3B82F6',
          buttonHover: '#2563EB'
        };
      case 'confirm':
        return {
          bg: '#F9FAFB',
          border: '#E5E7EB',
          text: '#374151',
          button: '#3B82F6',
          buttonHover: '#2563EB'
        };
      default:
        return {
          bg: '#F9FAFB',
          border: '#E5E7EB',
          text: '#374151',
          button: '#3B82F6',
          buttonHover: '#2563EB'
        };
    }
  };

  const colors = getColors();

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      case 'confirm':
        return '❓';
      default:
        return 'ℹ️';
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 100,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        width: '100%',
        maxWidth: '400px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          backgroundColor: colors.bg,
          borderLeft: `4px solid ${colors.border}`
        }}>
          <span style={{ fontSize: '1.25rem' }}>{getIcon()}</span>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: 600,
            color: colors.text,
            margin: 0
          }}>
            {title}
          </h3>
        </div>

        {/* Modal Content */}
        <div style={{
          padding: '1.5rem'
        }}>
          <p style={{
            color: '#6B7280',
            fontSize: '14px',
            lineHeight: '1.5',
            margin: 0
          }}>
            {message}
          </p>
        </div>

        {/* Modal Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: type === 'confirm' ? 'space-between' : 'flex-end',
          gap: '0.75rem',
          backgroundColor: '#F9FAFB'
        }}>
          {type === 'confirm' && (
            <button
              onClick={onCancel || onClose}
              style={{
                padding: '10px 20px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                color: '#374151',
                fontWeight: 500,
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#FFFFFF'}
            >
              {cancelText}
            </button>
          )}
          
          <button
            onClick={onConfirm || onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: colors.button,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 500,
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = colors.buttonHover}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = colors.button}
          >
            {type === 'confirm' ? confirmText : 'Tutup'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateGroupModal: React.FC<{
  mode: 'create' | 'edit';
  editingGroup: { id: string; name: string } | null;
  onSuccess: (groupId: string, groupName: string, patrolCount: number, mode: 'create' | 'edit') => void;
  onClose: () => void;
}> = ({ mode, editingGroup, onSuccess, onClose }) => {
  const [groupName, setGroupName] = useState("");
  const [selectedPatrols, setSelectedPatrols] = useState<number[]>([]);
  const [patrols, setPatrols] = useState<Patrol[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchPatrols = async () => {
    try {
      setFetchLoading(true);
      const res = await axiosInstance.get("/api/v1/patrol/index", {
        params: { per_page: 50 },
      });
      setPatrols(res.data.data || []);
    } catch (err: any) {
      setError("Gagal memuat data patrol");
      console.error("❌ Error fetching patrols:", err);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchGroupDetails = async (groupId: string) => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/patrol/group/show/${groupId}`
      );
      const groupData = res.data?.data || res.data;

      if (!groupData) {
        console.error("❌ Group data not found in API response");
        return;
      }

      setGroupName(groupData.name || "");
      setSelectedPatrols(
        Array.isArray(groupData.patrols)
          ? groupData.patrols.map((p: any) => p.id)
          : []
      );
    } catch (err) {
      console.error("❌ Error fetching group details:", err);
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchPatrols();
      if (editingGroup) {
        await fetchGroupDetails(editingGroup.id);
      }
    };
    init();
  }, [editingGroup]);

  const handlePatrolSelect = (patrolId: number) => {
    setSelectedPatrols((prev) =>
      prev.includes(patrolId)
        ? prev.filter((id) => id !== patrolId)
        : [...prev, patrolId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPatrols.length === patrols.length) {
      setSelectedPatrols([]);
    } else {
      setSelectedPatrols(patrols.map((p) => p.id));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!groupName.trim()) {
      setError("Nama group harus diisi");
      return;
    }

    if (selectedPatrols.length === 0) {
      setError("Pilih minimal 1 patrol");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = {
        name: groupName.trim(),
        patrols: selectedPatrols.map((id) => ({ id })),
      };

      let response;
      let groupId = editingGroup?.id;

      if (editingGroup) {
        response = await axiosInstance.post(
          `/api/v1/patrol/group/update/${editingGroup.id}`,
          payload
        );
      } else {
        response = await axiosInstance.post(
          "/api/v1/patrol/group/store",
          payload
        );
      }

      const data = response.data?.data || response.data;
      const newGroupId = data?.id || editingGroup?.id || "new";

      if (data && newGroupId) {
        onSuccess(newGroupId, groupName.trim(), selectedPatrols.length, mode);
      }
    } catch (err: any) {
      console.error("❌ GROUP SUBMIT ERROR:", err);
      setError(err.response?.data?.message || "Gagal menyimpan group");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 50,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#F9FAFB'
        }}>
          <h2 style={{
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#1F2937',
            margin: 0
          }}>
            {mode === 'create' ? 'Buat Group Baru' : 'Edit Group'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#6B7280',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span style={{ fontSize: '20px', fontWeight: 'bold' }}>×</span>
          </button>
        </div>

        {/* Modal Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1.5rem'
        }}>
          {fetchLoading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '2rem'
            }}>
              <div>Loading patrols...</div>
            </div>
          ) : (
            <>
              {error && (
                <div style={{
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#DC2626',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '1.5rem'
                }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Input Nama Group */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 500,
                    color: '#374151',
                    fontSize: '14px'
                  }}>
                    Nama Group *
                  </label>
                  <input
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      backgroundColor: '#FFFFFF',
                      transition: 'border-color 0.2s'
                    }}
                    placeholder="Masukkan nama group"
                    required
                    onFocus={(e) => e.target.style.borderColor = '#3B82F6'}
                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                  />
                </div>

                {/* Patrol Selection */}
                <div style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  backgroundColor: '#F9FAFB'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem'
                  }}>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: 500,
                      color: '#374151',
                      margin: 0
                    }}>
                      Pilih Patrol ({selectedPatrols.length} terpilih)
                    </h3>
                    <button
                      type="button"
                      onClick={handleSelectAll}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        backgroundColor: '#FFFFFF',
                        color: '#374151',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#FFFFFF'}
                    >
                      {selectedPatrols.length === patrols.length
                        ? "Batal Pilih Semua"
                        : "Pilih Semua"}
                    </button>
                  </div>

                  <div style={{ 
                    maxHeight: '200px', 
                    overflowY: 'auto',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '6px',
                    padding: '0.5rem'
                  }}>
                    {patrols.length === 0 ? (
                      <p style={{
                        textAlign: 'center',
                        color: '#6B7280',
                        padding: '1rem',
                        fontSize: '14px'
                      }}>
                        Tidak ada data patrol
                      </p>
                    ) : (
                      patrols.map((patrol) => (
                        <div
                          key={patrol.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <input
                            type="checkbox"
                            id={`patrol-${patrol.id}`}
                            checked={selectedPatrols.includes(patrol.id)}
                            onChange={() => handlePatrolSelect(patrol.id)}
                            style={{ 
                              marginRight: '12px',
                              width: '16px',
                              height: '16px'
                            }}
                          />
                          <label 
                            htmlFor={`patrol-${patrol.id}`}
                            style={{
                              color: '#374151',
                              fontSize: '14px',
                              cursor: 'pointer',
                              flex: 1
                            }}
                          >
                            {patrol.name}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Submit Buttons */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '0.75rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid #E5E7EB'
                }}>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    style={{
                      padding: '10px 20px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                      color: '#374151',
                      fontWeight: 500,
                      fontSize: '14px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.6 : 1,
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => !loading && (e.currentTarget.style.backgroundColor = '#F9FAFB')}
                    onMouseOut={(e) => !loading && (e.currentTarget.style.backgroundColor = '#FFFFFF')}
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    disabled={
                      loading || !groupName.trim() || selectedPatrols.length === 0
                    }
                    style={{
                      padding: '10px 20px',
                      backgroundColor:
                        loading || !groupName.trim() || selectedPatrols.length === 0
                          ? '#9CA3AF'
                          : '#3B82F6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 500,
                      fontSize: '14px',
                      cursor:
                        loading || !groupName.trim() || selectedPatrols.length === 0
                          ? 'not-allowed'
                          : 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => {
                      if (!loading && groupName.trim() && selectedPatrols.length > 0) {
                        e.currentTarget.style.backgroundColor = '#2563EB';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!loading && groupName.trim() && selectedPatrols.length > 0) {
                        e.currentTarget.style.backgroundColor = '#3B82F6';
                      }
                    }}
                  >
                    {loading
                      ? mode === 'edit'
                        ? "Menyimpan..."
                        : "Membuat..."
                      : mode === 'edit'
                      ? "Simpan Perubahan"
                      : "Buat Group & Lanjut Assign Users"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AssignUsersModal: React.FC<{
  groupId: string;
  groupName: string;
  onClose: () => void;
  onUpdate: () => void;
  canAssignUsers?: boolean;
  canRemoveUsers?: boolean;
}> = ({ groupId, groupName, onClose, onUpdate, canAssignUsers = true, canRemoveUsers = true }) => {
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [assignedUsers, setAssignedUsers] = useState<User[]>([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  const fetchSecurityUsers = async () => {
    try {
      setFetchLoading(true);
      const res = await axiosInstance.get("/api/ap/v1/employee", {
        params: { type:'collection', role: "security" },
      });

      const userData = (res.data || []).map((u: any) => {
        const fullName = 
          u.employee_personal?.first_name ||
          u.user?.name ||
          (u.user?.email ? u.user.email.split('@')[0] : null) ||
          `Security User ${u.user_id}`;
        
        const userId = u.user_id || u.id;
        
        return {
          id: userId,
          name: `${fullName} (${userId})`,
          fullName: fullName,
          userId: userId
        };
      });

      setUsers(userData);
      
    } catch (err: any) {
      console.error("❌ Error fetching users:", err);
      setError("Gagal memuat data user security.");
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchAssignedUsers = async () => {
    if (!groupId) return;
    try {
      const res = await axiosInstance.get(
        `/api/v1/patrol/group/component/${groupId}/index`
      );

      const assignedData = (res.data?.data || []).map((item: any) => {
        const userId = item.user?.id || item.user_id;
        const userFromList = users.find(u => u.id === userId);
        
        let userName = "Unknown";
        
        if (userFromList) {
          userName = userFromList.fullName || userFromList.name.replace(` (${userId})`, '');
        } else {
          userName = 
            item.user?.employee_personal?.first_name ||
            item.user?.name ||
            item.employee_personal?.first_name ||
            item.name ||
            `User ${userId}`;
        }
        
        return {
          id: item.id,
          userId: userId,
          name: `${userName} (${userId})`,
          fullName: userName
        };
      });

      setAssignedUsers(assignedData);
      
    } catch (err) {
      console.error("❌ Error fetching assigned users:", err);
    }
  };

  useEffect(() => {
    if (users.length > 0 && groupId) {
      fetchAssignedUsers();
    }
  }, [users, groupId]);

  useEffect(() => {
    if (groupId) {
      fetchSecurityUsers();
    }
  }, [groupId]);

  const handleUserSelect = (userId: number) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((user) => user.id));
    }
  };

  const assignUsersToGroup = async () => {
    if (!groupId || selectedUsers.length === 0) return;

    setAssigning(true);
    setError("");
    setSuccess("");

    const results: any[] = [];

    try {
      for (const userId of selectedUsers) {
        try {
          const payload = { user_id: userId };
          await axiosInstance.post(
            `/api/v1/patrol/group/component/${groupId}/store`,
            payload
          );
          results.push({ userId, success: true });
        } catch (err: any) {
          const msg =
            err.response?.data?.message ||
            `Gagal assign user ${userId} (mungkin sudah diassign)`;
          results.push({ userId, success: false, error: msg });
          console.error(`❌ Failed assign user ${userId}:`, msg);
        }
        await new Promise((r) => setTimeout(r, 200));
      }

      const successCount = results.filter((r) => r.success).length;
      const failCount = results.length - successCount;

      if (failCount === 0) {
        setSuccess(`✅ Berhasil assign ${successCount} user ke group.`);
      } else if (successCount > 0) {
        const failMessages = results
          .filter((r) => !r.success)
          .map((r) => r.error)
          .join(", ");
        setSuccess(
          `Sebagian berhasil (${successCount} sukses, ${failCount} gagal).`
        );
        setError(failMessages);
      } else {
        const failMessages = results.map((r) => r.error).join(", ");
        setError(failMessages);
      }

      setSelectedUsers([]);
      fetchAssignedUsers();
      onUpdate();
    } catch (err) {
      console.error("🔥 Unexpected error:", err);
      setError("Terjadi kesalahan saat assign user ke group.");
    } finally {
      setAssigning(false);
    }
  };

  const removeUserFromGroup = async (componentId: number, userName: string) => {
    if (!groupId) return;
  
    if (window.confirm) {
      const confirmDelete = window.confirm(
        `Yakin ingin menghapus ${userName} dari group?`
      );
      if (!confirmDelete) return;
    }

    try {
      await axiosInstance.delete(
        `/api/v1/patrol/group/component/${groupId}/destroy/${componentId}`
      );
      setAssignedUsers((prev) => prev.filter((u) => u.id !== componentId));
      onUpdate();
    } catch (err) {
      console.error("❌ Gagal hapus user dari group:", err);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 50,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        width: '100%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#F9FAFB'
        }}>
          <div>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#1F2937',
              margin: 0,
              marginBottom: '0.25rem'
            }}>
              Assign Users ke Group
            </h2>
            <p style={{
              fontSize: '14px',
              color: '#6B7280',
              margin: 0
            }}>
              {groupName} (ID: {groupId})
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#6B7280',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span style={{ fontSize: '20px', fontWeight: 'bold' }}>×</span>
          </button>
        </div>

        {/* Modal Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1.5rem'
        }}>
          {fetchLoading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '2rem'
            }}>
              <div>Loading users...</div>
            </div>
          ) : (
            <>
              {/* Error / Success Messages */}
              {error && (
                <div style={{
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#DC2626',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '1.5rem'
                }}>
                  {error}
                </div>
              )}
              {success && (
                <div style={{
                  backgroundColor: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                  color: '#166534',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '1.5rem'
                }}>
                  {success}
                </div>
              )}

              {/* User Selection */}
              <div style={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                backgroundColor: '#F9FAFB'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem'
                }}>
                  <h3 style={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#374151',
                    margin: 0
                  }}>
                    Pilih User Security ({selectedUsers.length} terpilih)
                  </h3>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      backgroundColor: '#FFFFFF',
                      color: '#374151',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#FFFFFF'}
                  >
                    {selectedUsers.length === users.length
                      ? "Batal Pilih Semua"
                      : "Pilih Semua"}
                    </button>
                </div>

                <div style={{ 
                  maxHeight: '300px',
                  overflowY: 'auto',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '6px',
                  border: '1px solid #E5E7EB'
                }}>
                  {users.length === 0 ? (
                    <p style={{
                      textAlign: 'center',
                      color: '#6B7280',
                      padding: '2rem',
                      fontSize: '14px'
                    }}>
                      Tidak ada data user security
                    </p>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      {users.map((user) => (
                        <div
                          key={user.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px 16px',
                            borderBottom: '1px solid #F3F4F6',
                            transition: 'background-color 0.2s',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleUserSelect(user.id)}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = selectedUsers.includes(user.id) ? '#BFDBFE' : '#F8FAFC'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = selectedUsers.includes(user.id) ? '#DBEAFE' : '#FFFFFF'}
                        >
                          <input
                            type="checkbox"
                            id={`user-${user.id}`}
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => handleUserSelect(user.id)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ 
                              marginRight: '12px',
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer'
                            }}
                          />
                          <label 
                            htmlFor={`user-${user.id}`}
                            style={{
                              color: '#374151',
                              fontSize: '14px',
                              cursor: 'pointer',
                              flex: 1,
                              fontWeight: selectedUsers.includes(user.id) ? 500 : 400
                            }}
                          >
                            {user.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div style={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                padding: '1.5rem',
                backgroundColor: '#F9FAFB'
              }}>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#374151',
                  margin: 0,
                  marginBottom: '1rem'
                }}>
                  User yang sudah diassign ({assignedUsers.length})
                </h3>
                
                <div style={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '6px',
                  border: '1px solid #E5E7EB'
                }}>
                  {assignedUsers.length === 0 ? (
                    <p style={{
                      textAlign: 'center',
                      color: '#6B7280',
                      padding: '2rem',
                      fontSize: '14px'
                    }}>
                      Belum ada user yang diassign ke group ini.
                    </p>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      {assignedUsers.map((user, index) => (
                        <div
                          key={user.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px 16px',
                            borderBottom: index < assignedUsers.length - 1 ? '1px solid #F3F4F6' : 'none',
                            backgroundColor: '#FFFFFF'
                          }}
                        >
                          <span style={{
                            color: '#374151',
                            fontSize: '14px',
                            fontWeight: 500
                          }}>
                            {user.name}
                          </span>
                          {canRemoveUsers && (
                            <button
                              onClick={() => removeUserFromGroup(user.id, user.fullName || user.name)}
                              style={{
                                backgroundColor: '#FEE2E2',
                                color: '#DC2626',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#FECACA'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#FEE2E2'}
                            >
                              Hapus
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div style={{
          padding: '1.5rem',
          borderTop: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#F9FAFB'
        }}>
          <button
            onClick={onClose}
            disabled={assigning}
            style={{
              padding: '10px 20px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              backgroundColor: '#FFFFFF',
              color: '#374151',
              fontWeight: 500,
              fontSize: '14px',
              cursor: assigning ? 'not-allowed' : 'pointer',
              opacity: assigning ? 0.6 : 1,
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => !assigning && (e.currentTarget.style.backgroundColor = '#F9FAFB')}
            onMouseOut={(e) => !assigning && (e.currentTarget.style.backgroundColor = '#FFFFFF')}
          >
            Tutup
          </button>

          {canAssignUsers && (
            <button
              onClick={assignUsersToGroup}
              disabled={assigning || selectedUsers.length === 0}
              style={{
                padding: '10px 20px',
                backgroundColor:
                  assigning || selectedUsers.length === 0
                    ? '#9CA3AF'
                    : '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 500,
                fontSize: '14px',
                cursor:
                  assigning || selectedUsers.length === 0
                    ? 'not-allowed'
                    : 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => {
                if (!assigning && selectedUsers.length > 0) {
                  e.currentTarget.style.backgroundColor = '#2563EB';
                }
              }}
              onMouseOut={(e) => {
                if (!assigning && selectedUsers.length > 0) {
                  e.currentTarget.style.backgroundColor = '#3B82F6';
                }
              }}
            >
              {assigning
                ? `Assigning ${selectedUsers.length} Users...`
                : `Assign ${selectedUsers.length} Users`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const GroupSpace: React.FC = () => {
  const [groups, setGroups] = useState<PatrolGroup[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [groupUserCounts, setGroupUserCounts] = useState<{ [key: string]: number }>({});
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<{id: string, name: string} | null>(null);
  const [selectedGroupForUsers, setSelectedGroupForUsers] = useState<{id: string, name: string} | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [alert, setAlert] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'warning' | 'info' | 'confirm';
    title: string;
    message: string;
    onConfirm?: () => void;
  }>({
    show: false,
    type: 'info',
    title: '',
    message: ''
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { canIndex, canStore, canUpdate, canDelete } = usePermission('patrol_group_index');
  const { 
    canIndex: canComponentIndex, 
    canStore: canComponentStore,
    canDelete: canComponentDelete 
  } = usePermission('patrol_group_component_index');

  const fetchGroups = async (searchFilter = "") => {
    if (searchFilter) {
      setSearchLoading(true);
    } else {
      setInitialLoading(true);
    }
    
    try {
      const params: any = {
        sort_by: "created_at",
        sort_type: "desc",
        per_page: 50 
      };

      if (searchFilter) {
        params.filter = searchFilter;
      }

      const res = await axiosInstance.get("/api/v1/patrol/group/index", { params });
      const groupData = res.data.data || [];
      setGroups(groupData);

      const counts: { [key: string]: number } = {};
      await Promise.all(
        groupData.map(async (group: PatrolGroup) => {
          try {
            const resCount = await axiosInstance.get(
              `/api/v1/patrol/group/component/${group.id}/index`
            );
            counts[group.id] = resCount.data?.data?.length || 0;
          } catch (err) {
            counts[group.id] = 0;
          }
        })
      );
      setGroupUserCounts(counts);

    } catch (err: any) {
      setError('Gagal memuat data groups');
    } finally {
      setInitialLoading(false);
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
  const timeoutId = setTimeout(() => {
    if (searchQuery !== null) {
      if (searchQuery.trim() === '') {
        setSearchLoading(true);
        fetchGroups(""); 
      } else if (searchQuery.length >= 3) {
        setSearchLoading(true);
        fetchGroups(searchQuery);
      } else {
        setSearchLoading(false);
      }
    }
  }, 600);

  return () => clearTimeout(timeoutId);
}, [searchQuery]);

  if (!canIndex) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontFamily: 'Inter, sans-serif'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          backgroundColor: '#FEF2F2',
          borderRadius: '8px',
          border: '1px solid #FECACA'
        }}>
          <h3 style={{ color: '#DC2626', marginBottom: '1rem' }}>Akses Ditolak</h3>
          <p style={{ color: '#6B7280' }}>Anda tidak memiliki izin untuk mengakses halaman Patrol Groups.</p>
        </div>
      </div>
    );
  }

  const showAlert = (type: 'success' | 'error' | 'warning' | 'info' | 'confirm', title: string, message: string, onConfirm?: () => void) => {
    setAlert({
      show: true,
      type,
      title,
      message,
      onConfirm
    });
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const handleBack = () => { 
    dispatch(actions.setScreen(TabScreens.Admin)); 
    navigate("/tab-navigator", { replace: true }); 
  };

  const handleDeleteGroup = async (groupId: string, groupName: string) => {
    showAlert(
      'confirm',
      'Konfirmasi Hapus',
      `Yakin ingin menghapus group "${groupName}"? Tindakan ini tidak dapat dibatalkan.`,
      async () => {
        try {
          setDeleteLoading(groupId);
          await axiosInstance.delete(`/api/v1/patrol/group/destroy/${groupId}`);
          fetchGroups(searchQuery);
          showAlert('success', 'Berhasil', 'Group berhasil dihapus');
        } catch (err: any) {
          showAlert('error', 'Gagal', err.response?.data?.message || 'Gagal menghapus group');
        } finally {
          setDeleteLoading(null);
        }
      }
    );
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setEditingGroup(null);
    setShowGroupModal(true);
  };

  const handleOpenEditModal = (groupId: string, groupName: string) => {
    setModalMode('edit');
    setEditingGroup({ id: groupId, name: groupName });
    setShowGroupModal(true);
  };

  const handleOpenUsersModal = (groupId: string, groupName: string) => {
    setSelectedGroupForUsers({ id: groupId, name: groupName });
    setShowUsersModal(true);
  };

  const handleModalSuccess = async (
    groupId: string,
    groupName: string,
    patrolCount: number,
    mode: 'create' | 'edit'
  ) => {
    setShowGroupModal(false);

    try {
      await fetchGroups(searchQuery);
      
      showAlert('success', 'Berhasil', 
        mode === 'create' 
          ? 'Patrol group berhasil dibuat' 
          : 'Group berhasil diperbarui'
      );
    } catch (err) {
      showAlert('error', 'Gagal', 'Terjadi kesalahan saat memperbarui data group');
    }
  };

  const handleCloseGroupModal = () => {
    setShowGroupModal(false);
    setEditingGroup(null);
  };

  const handleCloseUsersModal = () => {
    setShowUsersModal(false);
    setSelectedGroupForUsers(null);
  };

  if (initialLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '200px',
        fontFamily: 'Inter, sans-serif'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{
      height: '100vh',
      backgroundColor: '#F9FAFB',
      fontFamily: 'Inter, sans-serif',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <header style={{
        backgroundColor: '#FFFFFF',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        flexShrink: 0
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1rem 0'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <button 
                onClick={handleBack}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1F2937',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <span style={{ 
                  fontSize: '24px',
                  fontWeight: 'bold'
                }}>←</span>
              </button>
              <h1 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: '#1F2937',
                margin: 0
              }}>
                Patrol Groups
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%',
        overflow: 'hidden'
      }}>
        {/* Create Group Button */}
        <div style={{ marginBottom: '1rem', flexShrink: 0 }}>
          {canStore && (
            <button
              onClick={handleOpenCreateModal}
              style={{
                width: '100%',
                backgroundColor: '#3B82F6',
                color: 'white',
                fontWeight: 600,
                padding: '12px 16px',
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                display: 'flex',
                alignItems: 'center', 
                justifyContent: 'center',
                fontSize: '16px',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3B82F6'}
            >
              + Buat Group Baru
            </button>
          )}
        </div>

        {/* Search Bar */}
        <div style={{
          marginBottom: '1.5rem',
          flexShrink: 0,
          width: '100%',
          position: 'relative'
        }}>
          <div style={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            alignItems: 'center'
          }}>
            <div style={{
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2
            }}>
              {searchLoading ? (
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #3B82F6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              ) : (
                <div style={{
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <SearchSvg />
                </div>
              )}
            </div>
            <input
              type="text"
              placeholder="Cari group berdasarkan nama..."
              style={{
                width: '100%',
                padding: '12px 16px 12px 48px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                height: '48px',
                boxSizing: 'border-box'
              }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={(e) => e.target.style.borderColor = '#3B82F6'}
              onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
            />
            
          </div>
          {/* {searchQuery.length > 0 && searchQuery.length < 3 && (
            <div style={{
              marginTop: '8px',
              padding: '8px 12px',
              backgroundColor: '#FFFBEB',
              border: '1px solid #F59E0B',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#D97706'
            }}>
              ⚡ Ketik minimal 3 karakter untuk memulai pencarian
            </div>
          )} */}
        </div>

        {error && (
          <div style={{
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            color: '#DC2626',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            flexShrink: 0
          }}>
            {error}
          </div>
        )}
        
        <div style={{
          flex: 1,
          overflowY: 'auto',
          paddingRight: '4px'
        }}>
          
          {/* EMPTY STATE */}
          {!initialLoading && groups.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '60px',
              color: '#6B7280',
              fontSize: '16px',
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: '16px',
              flex: 1
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937' }}>
                  {searchQuery ? "Group Tidak Ditemukan" : "Belum Ada Group"}
                </div>
                <p style={{ 
                  color: '#6B7280', 
                  margin: 0,
                  fontSize: '16px',
                  textAlign: 'center'
                }}>
                  {searchQuery 
                    ? `Tidak ada group yang sesuai dengan pencarian "${searchQuery}"`
                    : 'Mulai dengan membuat group pertama Anda'
                  }
                </p>
                
                {/* TOMBOL */}
                {!searchQuery && canStore && (
                  <button
                    onClick={handleOpenCreateModal}
                    style={{
                      backgroundColor: '#3B82F6',
                      color: 'white',
                      fontWeight: 600,
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      transition: 'background-color 0.3s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center', 
                      justifyContent: 'center',
                      height: '44px',
                      width: '100%',
                      maxWidth: '100%'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3B82F6'}
                  >
                    Buat Group Pertama
                  </button>
                )}
                {searchQuery && (
                  <button 
                    onClick={() => {
                      setSearchQuery("");
                      fetchGroups();
                    }}
                    style={{
                      backgroundColor: '#3B82F6',
                      color: 'white',
                      fontWeight: 600,
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      transition: 'background-color 0.3s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center', 
                      justifyContent: 'center',
                      height: '44px',
                      width: '100%',
                      maxWidth: '100%'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3B82F6'}
                  >
                    Tampilkan Semua Group
                  </button>
                )}
              </div>
            </div>
          )}

          {/* DATA STATE */}
          {!initialLoading && groups.length > 0 && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1.5rem',
              paddingBottom: '1rem'
            }}>

              {searchQuery && (
                <div style={{
                  display: 'flex',
                  flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                  justifyContent: 'space-between',
                  alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center',
                  gap: '12px',
                  marginBottom: '1rem',
                  padding: '12px 16px',
                  backgroundColor: '#F3F4F6',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB'
                }}>
                  <p style={{ 
                    margin: 0, 
                    color: '#374151', 
                    fontSize: '14px',
                    width: window.innerWidth <= 768 ? '100%' : 'auto'
                  }}>
                    Menampilkan {groups.length} hasil untuk "{searchQuery}"
                  </p>
                  <button 
                    onClick={() => {
                      setSearchQuery("");
                      fetchGroups();
                    }} 
                    style={{
                      background: '#6B7280',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s',
                      alignSelf: window.innerWidth <= 768 ? 'flex-end' : 'auto'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#4B5563'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#6B7280'}
                  >
                    Bersihkan
                  </button>
                </div>
              )}

              {groups.map((group) => (
                <div key={group.id} style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  padding: '1.5rem'
                }}>
                  {/* Group Header */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}>
                    <h2 style={{
                      fontSize: '1.125rem',
                      fontWeight: 600,
                      color: '#1F2937',
                      margin: 0
                    }}>
                      {group.name}
                    </h2>
                  </div>

                  {/* Group Info */}
                  <div style={{
                    marginTop: '1rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    fontSize: '14px',
                    color: '#6B7280'
                  }}>
                    {group.created_at && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}>
                        <span style={{ fontWeight: 500 }}>Dibuat:</span>
                        <span>{new Date(group.created_at).toLocaleDateString('id-ID')}</span>
                      </div>
                    )}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{ fontWeight: 500 }}>Security Assigned:</span>
                      <span style={{
                        fontWeight: 600,
                        color: '#3B82F6'
                      }}>
                        {groupUserCounts[group.id] ?? 0}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{
                    marginTop: '1.5rem',
                    display: 'flex',
                    gap: '0.75rem'
                  }}>
                    {canComponentIndex && (
                      <button
                        onClick={() => handleOpenUsersModal(group.id, group.name)}
                        style={{
                          flex: 1,
                          backgroundColor: '#DBEAFE',
                          color: '#3B82F6',
                          fontWeight: 600,
                          padding: '8px 16px',
                          borderRadius: '12px',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center', 
                          justifyContent: 'center',
                          fontSize: '14px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#BFDBFE'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#DBEAFE'}
                      >
                        Users
                      </button>
                    )}
                    
                    {canUpdate && (
                      <button
                        onClick={() => handleOpenEditModal(group.id, group.name)}
                        style={{
                          flex: 1,
                          backgroundColor: '#E5E7EB',
                          color: '#1F2937',
                          fontWeight: 600,
                          padding: '8px 16px',
                          borderRadius: '12px',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center', 
                          justifyContent: 'center',
                          fontSize: '14px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#D1D5DB'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#E5E7EB'}
                      >
                        Edit
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDeleteGroup(group.id, group.name)}
                        disabled={deleteLoading === group.id}
                        style={{
                          flex: 1,
                          backgroundColor: deleteLoading === group.id ? '#F3F4F6' : '#FEE2E2',
                          color: deleteLoading === group.id ? '#9CA3AF' : '#DC2626',
                          fontWeight: 600,
                          padding: '8px 16px',
                          borderRadius: '12px',
                          border: 'none',
                          cursor: deleteLoading === group.id ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center', 
                          justifyContent: 'center',
                          transition: 'background-color 0.2s',
                          opacity: deleteLoading === group.id ? 0.6 : 1
                        }}
                        onMouseOver={(e) => {
                          if (deleteLoading !== group.id) {
                            e.currentTarget.style.backgroundColor = '#FECACA';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (deleteLoading !== group.id) {
                            e.currentTarget.style.backgroundColor = '#FEE2E2';
                          }
                        }}
                      >
                        {deleteLoading === group.id ? 'Menghapus...' : 'Hapus'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Group Modal */}
      {showGroupModal && (
        <CreateGroupModal
          mode={modalMode}
          editingGroup={editingGroup}
          onSuccess={handleModalSuccess}
          onClose={handleCloseGroupModal}
        />
      )}

      {/* Assign Users Modal */}
      {showUsersModal && selectedGroupForUsers && (
        <AssignUsersModal
          groupId={selectedGroupForUsers.id}
          groupName={selectedGroupForUsers.name}
          onClose={handleCloseUsersModal}
          onUpdate={fetchGroups}
          canAssignUsers={canComponentStore}
          canRemoveUsers={canComponentDelete}
        />
      )}

      {/* Custom Alert Modal */}
      {alert.show && (
        <AlertModal
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onConfirm={alert.onConfirm}
          onClose={closeAlert}
          onCancel={closeAlert}
        />
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};